import React, { useState, useRef, useEffect } from 'react';

interface DropdownItem {
  id: string;
  label: string;
  icon?: React.ReactNode;
  onClick: () => void;
  disabled?: boolean;
  variant?: 'default' | 'danger';
  separator?: boolean; // Add separator after this item
}

interface DropdownSection {
  title?: string;
  items: DropdownItem[];
}

interface DropdownProps {
  trigger: React.ReactNode;
  sections: DropdownSection[];
  align?: 'left' | 'right';
  className?: string;
}

const Dropdown: React.FC<DropdownProps> = ({ trigger, sections, align = 'right', className = '' }) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const toggleDropdown = () => {
    setIsOpen(!isOpen);
  };

  const handleItemClick = (item: DropdownItem) => {
    if (!item.disabled) {
      item.onClick();
      setIsOpen(false);
    }
  };

  const getItemClassName = (item: DropdownItem) => {
    const baseClasses = 'w-full flex items-center px-4 py-2 text-sm transition-colors duration-150';

    if (item.disabled) {
      return `${baseClasses} text-gray-400 cursor-not-allowed`;
    }

    if (item.variant === 'danger') {
      return `${baseClasses} text-red-600 hover:bg-red-50 hover:text-red-700 cursor-pointer`;
    }

    return `${baseClasses} text-gray-700 hover:bg-gray-50 hover:text-gray-900 cursor-pointer`;
  };

  const alignmentClasses = {
    left: 'left-0',
    right: 'right-0',
  };

  return (
    <div className={`relative ${className}`} ref={dropdownRef}>
      {/* Trigger */}
      <div onClick={toggleDropdown} className="cursor-pointer">
        {trigger}
      </div>

      {/* Dropdown Menu */}
      {isOpen && (
        <div
          className={`absolute top-full mt-2 w-56 bg-white rounded-lg shadow-lg border border-gray-200 z-50 ${alignmentClasses[align]}`}
        >
          <div className="py-1">
            {sections.map((section, sectionIndex) => (
              <div key={sectionIndex}>
                {/* Section Title */}
                {section.title && (
                  <div className="px-4 py-2 border-b border-gray-100">
                    <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wide">{section.title}</h3>
                  </div>
                )}

                {/* Section Items */}
                <div className={section.title ? 'py-1' : ''}>
                  {section.items.map((item, itemIndex) => (
                    <div key={item.id}>
                      <button
                        onClick={() => handleItemClick(item)}
                        className={getItemClassName(item)}
                        disabled={item.disabled}
                      >
                        {item.icon && <span className="mr-3 flex-shrink-0">{item.icon}</span>}
                        <span className="flex-1 text-left">{item.label}</span>
                      </button>

                      {/* Separator */}
                      {item.separator && itemIndex < section.items.length - 1 && (
                        <div className="border-t border-gray-100 my-1" />
                      )}
                    </div>
                  ))}
                </div>

                {/* Section Separator */}
                {sectionIndex < sections.length - 1 && <div className="border-t border-gray-100" />}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default Dropdown;
